import { CSSInterpolation } from '@emotion/css/create-instance'
import React, { type ChangeEvent, type Ref, useCallback } from 'react'

import type { ReactInputProps } from '../../../types'
import { INPUT_MIN_USABLE_WIDTH } from '../../../util/commonStyles'
import { CssVars } from '../../themes'
import { getStylesForTextType } from '../Type'
import { getInputContainerBorderStyles, getInputStyles } from './SharedStyles'

export const DEFAULT_SIZES = {
  border: 1,
  inputMarginLeft: 0,
  inputMarginRight: 0,
  inputMinWidth: INPUT_MIN_USABLE_WIDTH,
  paddingLeft: 12,
  paddingRight: 12,
}

export type InputState = 'default' | 'disabled' | 'error'

type Props = {
  /**
   * The value to show in the input. If empty, any provided `placeholder` is shown.
   */
  value: string
  /**
   * Callback called when the value in the input changes. As a convenience, the value is provided
   * separately from the change event for use cases that just need the new value.
   */
  onChange: ({
    event,
    value,
  }: {
    event: ChangeEvent<HTMLInputElement>
    value: string
  }) => void
  /**
   * Whether to disable the input or mark it as having an error.
   */
  state?: InputState
  /**
   * A React ref to attach to the actual `input` element, e.g. for triggering focus or blur on
   * the input.
   */
  innerRef?: Ref<HTMLInputElement>
  /**
   * A control to render to the right of the input, internal to the visual border.
   */
  endControl?: React.ReactNode
  /**
   * Element to render at start of the input, internal to the visual border.
   */
  startControl?: React.ReactNode
  /**
   * Optionally override the default sizes for various horizontal spacing.
   */
  sizes?: {
    border?: number
    paddingLeft?: number
    paddingRight?: number
    inputMarginLeft?: number
    inputMarginRight?: number
    inputMinWidth?: number
  }
  /**
   * Pass a CSS object if you need to override styles on the root element. Please do this sparingly.
   */
  overrideRootStyles?: CSSInterpolation
  /**
   * Pass a CSS object if you need to override styles on the input element. Please do this sparingly.
   */
  overrideInputStyles?: CSSInterpolation
}

type DefaultInputPropExclusions =
  | 'className'
  | 'css'
  | 'disabled'
  | 'onChange'
  | 'ref'
  | 'style'

type DefaultInputProps = Omit<
  ReactInputProps,
  keyof Props | DefaultInputPropExclusions
>

export type InternalInputProps = Props & DefaultInputProps;

/**
 * The core input component that underlies all `*Input` components in Bento Web.
 */
export function InternalInput({
  endControl,
  innerRef,
  onChange: onChangeProp,
  overrideInputStyles,
  overrideRootStyles,
  sizes: sizesProp,
  startControl,
  state,
  type,
  value,
  ...inputProps
}: InternalInputProps) {
  const sizes = {
    ...DEFAULT_SIZES,
    ...sizesProp,
  }

  const onChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      onChangeProp({ event, value: event.target.value })
    },
    [onChangeProp]
  )

  return (
    <div
      css={[
        getInputContainerBorderStyles(sizes.border, state),
        {
          alignItems: 'center',
          display: 'flex',
          height: CssVars.size.cellHeightSm,
          paddingLeft: sizes.paddingLeft,
          paddingRight: sizes.paddingRight,
          width: '100%',
        },
        overrideRootStyles,
      ]}
    >
      {startControl}
      <input
        {...inputProps}
        ref={innerRef}
        aria-invalid={state === 'error'}
        // TODO(eric.lovett): Remove this class name once legacy input styles are removed
        // from web-app.
        className="remove-legacy"
        css={[
          getStylesForTextType({ size: 'S' }),
          {
            height: '100%',
            marginLeft: sizes.inputMarginLeft,
            marginRight: sizes.inputMarginRight,
            minWidth: sizes.inputMinWidth,
            padding: 0,
          },
          getInputStyles(state),
          overrideInputStyles,
        ]}
        disabled={state === 'disabled'}
        onChange={onChange}
        type={type}
        value={value}
      />
      {endControl}
    </div>
  )
}
